/**
 * Filing a supplier's quotation from the workbook they returned — WITHOUT asking the server to
 * re-read the file.
 *
 * ---- Why the client builds this ----
 *
 * `supplier_quotation.parse_supplier_reply` downloads the attachment, parses it server-side and
 * writes the `Supplier Quotation`. It has failed twice in two days on the same field:
 *
 *   `B4`  delivery time written into a Date column      → MySQL 1292, whole parse rejected
 *   `B5`  `name 'fappe' is not defined`                 → NameError, whole parse rejected
 *
 * Both fire only once a supplier fills in DELIVERY TIME — the column our own template asks them to
 * fill in. Measured 2026-08-08: Akhil's reply, with that column populated, cannot be filed at all,
 * while his prices are already on screen because `rfqReplyWorkbook.ts` reads the very same file in
 * the browser without difficulty.
 *
 * So the file is read HERE and `create_supplier_quotation` is asked to record the result, rather
 * than to repeat the work. The server stops being the reader and becomes the recorder.
 *
 * ---- What this does not claim ----
 *
 * It only works for OUR OWN template, which we generate and therefore know the exact shape of. A
 * supplier who answers in prose, or attaches their own format, produces no workbook this can read —
 * those still need the server's parser, so `parse_supplier_reply` remains the fallback and both
 * `B4` and `B5` still deserve fixing.
 *
 * ---- Rates are taken verbatim ----
 *
 * No FX conversion, no per-RFQ-UOM restatement, no rounding. The rate recorded is the number the
 * supplier typed, in the currency their sheet names, against the UOM they quoted in. Everything
 * downstream (`compare.ts`) already converts and restates from those raw facts, and doing it twice
 * is how a price silently drifts from what the supplier actually offered.
 */

import { printedDescription } from './internalNotes'
import type { WorkbookOffer } from './rfqReplyWorkbook'
import type { CreateSupplierQuotationItemPayload, CreateSupplierQuotationPayload, EnquiryItem, Rfq, RfqItem } from './types'

export interface BuildQuotationInput {
  offer: WorkbookOffer
  /** The RFQ being answered — supplies supplier identity and each line's RFQ UOM. */
  rfq: Rfq
  enquiryId: string
  company: string
  /** `"YYYY-MM-DD"`. The supplier's quotation date — the day their reply arrived, not today. */
  transactionDate: string
  /** Used only when the workbook names no currency of its own. */
  fallbackCurrency: string
  /**
   * The enquiry's lines, so each filed row carries the description the purchaser printed — the
   * edit, else the customer's wording, never our notes (2026-10-16). Optional: a caller without
   * them files without descriptions and the server falls back to the Item master.
   */
  enquiryLines?: readonly Pick<EnquiryItem, 'cust_sno' | 'description' | 'raw_text'>[]
}

export type BuildQuotationResult =
  | { ok: true; payload: CreateSupplierQuotationPayload }
  | { ok: false; reason: string }

/** `"YYYY-MM-DD"` from a site-local `"YYYY-MM-DD HH:mm:ss"`, microseconds and all. */
export function toTransactionDate(received: string | null | undefined): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec((received ?? '').trim())
  return m ? m[1] : null
}

/**
 * Build the create payload, or explain why not.
 *
 * Refuses rather than guesses in four cases, each of which would otherwise record a quotation that
 * misstates what the supplier offered.
 */
export function buildQuotationFromWorkbook(input: BuildQuotationInput): BuildQuotationResult {
  const { offer, rfq, enquiryId, company, transactionDate, fallbackCurrency } = input

  /*
    The workbook states which RFQ it belongs to. A supplier who attaches the wrong file — an earlier
    RFQ, another customer's — must not have those prices filed against this one, and this is
    checkable rather than assumed.
  */
  if (offer.rfq_id !== null && offer.rfq_id !== rfq.id) {
    return { ok: false, reason: `That workbook is for ${offer.rfq_id}, not ${rfq.id}.` }
  }

  // A blank price is not a price. Recording it as 0 is exactly how a decline becomes an offer of
  // free goods that wins the allocation — the failure `useRfqReplies.ts`'s header documents.
  const priced = offer.lines.filter((line) => line.unit_price !== null && line.unit_price > 0)
  if (priced.length === 0) {
    return { ok: false, reason: 'They returned the workbook with no prices filled in.' }
  }

  const missingCode = priced.find((line) => line.item_code.trim() === '')
  if (missingCode !== undefined) {
    // Matching a line back by DESCRIPTION is the kind of guess that files a price against the wrong
    // item. The purchaser can key this one in by hand.
    return { ok: false, reason: `A priced line has no item code ("${missingCode.description}").` }
  }

  const currency = (offer.currency ?? '').trim() || fallbackCurrency
  if (currency === '') {
    return { ok: false, reason: 'The workbook names no currency, so the rates cannot be recorded.' }
  }

  /*
    Each reply row finds ITS RFQ row. Keyed on item_code alone this broke the moment one item sat
    on two lines (a variant — PAINT in two colours — allowed since 2026-10-16): a `Map` by code
    kept the LAST row, so both prices filed against one line and the other went unquoted. The
    supplier echoes our serial (`sno`, the enquiry row number we printed), so that is matched
    first; a sheet that lost the column takes the next unclaimed row of that code, in order.
  */
  const claim = rowClaimer(rfq.items ?? [])
  const descriptionByRow = new Map((input.enquiryLines ?? []).map((l) => [l.cust_sno, printedDescription(l)]))

  const items: CreateSupplierQuotationItemPayload[] = priced.map((line) => {
    const row = claim(line.item_code, line.sno)
    const rfqUom = row?.uom
    const description = row ? descriptionByRow.get(row.enquiry_row_no) ?? '' : ''
    return {
      item_code: line.item_code,
      // A priced line with no quantity is the supplier quoting a unit rate against our ask, so the
      // RFQ's own quantity is the honest reading — never 0, which would zero the line value.
      qty: line.qty ?? row?.qty ?? 0,
      uom: line.uom.trim() || rfqUom || '',
      rate: line.unit_price as number,
      ...(rfqUom === undefined ? {} : { rfq_uom: rfqUom }),
      /*
        THE THROUGH-KEY. Without it the quotation files successfully and is then invisible:
        `comparison.get_comparison` joins offers to enquiry rows on `enquiry_row_no`, so a line
        carrying none is dropped from `items[].offers` while its supplier still appears in
        `suppliers[]`. Measured 2026-08-08 — SQ-2026-00010 filed cleanly and the workbench showed
        one supplier on a two-supplier enquiry.

        Two independent sources, both preferred over guessing: the RFQ's own row number for this
        item, and the `Opportunity Item` docname the supplier hands straight back in the workbook's
        own column. Sent together because the server accepts either.
      */
      ...(row?.enquiry_row_no ? { enquiry_row_no: row.enquiry_row_no } : {}),
      ...(line.opportunity_item === '' ? {} : { opportunity_item: line.opportunity_item }),
      ...(description === '' ? {} : { description }),
      // Delivery time is folded into the remark rather than dropped: it is the field the server's
      // own parser chokes on (B4/B5), and losing "4 days" silently would repeat that loss quietly.
      ...(remarkOf(line) === '' ? {} : { remark: remarkOf(line) }),
    }
  })

  return {
    ok: true,
    payload: {
      rfq: rfq.id,
      enquiry: enquiryId,
      // The Link identity, never `supplier_name` — a display label here files the quotation against
      // a supplier that may not exist.
      supplier: rfq.supplier,
      transaction_date: transactionDate,
      currency,
      company,
      items,
    },
  }
}

/**
 * Hands out RFQ rows to reply lines, each row at most once: the row whose serial the supplier
 * echoed, else the first unclaimed row of that code, else (a serial we never printed, every row
 * of the code already taken) the first row of the code — the pre-2026-10-16 reading, kept so a
 * supplier who priced one line twice still files against something real.
 */
function rowClaimer(rows: readonly RfqItem[]): (code: string, sno: string) => RfqItem | undefined {
  const claimed = new Set<RfqItem>()
  return (code, sno) => {
    const ofCode = rows.filter((r) => r.code === code)
    const bySerial = sno.trim() === '' ? undefined : ofCode.find((r) => r.enquiry_row_no === sno.trim())
    const row = bySerial ?? ofCode.find((r) => !claimed.has(r)) ?? ofCode[0]
    if (row) claimed.add(row)
    return row
  }
}

/** The supplier's remark, with their delivery time kept alongside it rather than discarded. */
function remarkOf(line: WorkbookOffer['lines'][number]): string {
  const delivery = line.delivery.trim()
  const remark = line.remark.trim()
  if (delivery === '') return remark
  const asDays = /^\d+$/.test(delivery) ? `Delivery ${delivery} days` : `Delivery ${delivery}`
  return remark === '' ? asDays : `${asDays} · ${remark}`
}
